import { type FC, useState } from "react";
import { Alert } from "@mui/material";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ObjectFieldTemplateProps, RJSFSchema } from "@rjsf/utils";
import Api from "@/services/Api";
import { useMessenger } from "@/framework/Messenger/Messenger";
import CategoryObjectTemplate from "@/framework/KioForm/templates/object/CategoryObjectTemplate";
import type { KioFormContext } from "@/framework/KioForm/KioForm";
import type { KulturioUserPortalPollResponse } from "@/declarations/models/KulturioUserPortal";

type Answer = { answerId: string; answer: Record<string, string> };
type AnswersFormData = { answers?: Answer[]; general?: any };

const verifySchema = (schema: RJSFSchema) => {
  let validationMsg: string[] = [];
  if (schema?.properties?.answers?.type !== "array") {
    validationMsg.push("PollSubmitCategoryTemplate expects schema to have an 'answers' property of type 'array'");
  }
  if (schema?.properties?.answers?.items?.properties?.answerId?.type !== "string") {
    validationMsg.push(
      "PollSubmitCategoryTemplate expects schema to have an 'answers' property with items having an 'answerId' property of type 'string'"
    );
  }
  if (schema?.properties?.general?.properties?.showResults?.type !== "boolean") {
    validationMsg.push(
      "PollSubmitCategoryTemplate expects schema to have a 'general' property with a 'showResults' property of type 'boolean'"
    );
  }
  return validationMsg;
};

const addAnswerKeysAndTranslationsToContext = (context: KioFormContext, answers: Answer[], idSchemaId: string) => {
  const answerKeysWithTranslations: Record<string, any> = {};
  answers.forEach((answer) => {
    answerKeysWithTranslations[answer.answerId] = answer.answer;
  });
  if (!Object.hasOwn(context.sharedState, idSchemaId)) {
    context.sharedState[idSchemaId] = { answersIds: answerKeysWithTranslations };
  } else {
    context.sharedState[idSchemaId]["answersIds"] = answerKeysWithTranslations;
  }
};

const PollSubmitCategoryTemplate: FC<ObjectFieldTemplateProps> = (props) => {
  const formData = props.formData as AnswersFormData | undefined;
  const formContext: KioFormContext = props.formContext ?? {};
  const schema = props.schema ?? {};
  const validationMessage = verifySchema(schema);
  const { t } = useTranslation("common");
  const documentId = formContext.currentDocumentId;
  const msg = useMessenger();
  const [, setSubmitting] = useState(false);

  const list = formData?.answers ?? [];

  if (formData?.answers) {
    addAnswerKeysAndTranslationsToContext(formContext, formData?.answers, props.idSchema.$id);
  }

  const submit = async (newDocumentId?: number) => {
    const pollKeys = list.map((a) => a.answerId).filter((k): k is string => !!k);
    const showResultsCurrent = formData?.general?.showResults ?? true;
    if (pollKeys.length === 0) {
      msg.error(t("polls.errorNoAnswers"));
      return;
    }

    const currentDocumentId = documentId || newDocumentId; // ?? does not handle nan

    if (!currentDocumentId) {
      msg.error(t("polls.errorNoDocumentId"));
      return;
    }

    const pollResult = await Api.getOneKulturioUserPortalPollResponseNoCache(currentDocumentId, {
      show_logs: false,
    }).fetchDirect({} as KulturioUserPortalPollResponse);
    const showResultsExisting = pollResult?.show_result;
    const pollResultAnswerList = pollResult?.poll_data ? Object.keys(pollResult?.poll_data) : [];

    const missingKeysFromPollResult = pollResultAnswerList.filter((pRes: string) => !pollKeys.includes(pRes)) ?? [];
    const newKeysInPollKeys = pollKeys.filter((pKey: string) => !pollResultAnswerList.includes(pKey)) ?? [];
    const hasChangesInAnswers = !(missingKeysFromPollResult.length === 0 && newKeysInPollKeys.length === 0);
    if (!hasChangesInAnswers && showResultsCurrent === showResultsExisting) {
      // no changes. No need to save changes
      return;
    }

    setSubmitting(true);
    try {
      const [, err] = await Api.createKulturioUserPortalPollResponse(currentDocumentId, {
        poll_keys: pollKeys,
        show_result: showResultsCurrent,
      }).fetch();
      if (err) {
        throw err;
      }
      msg.success(t("polls.saveSuccess"));
    } catch (err: unknown) {
      msg.error(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };
  const idSchemaId = props.idSchema.$id;
  const callbackType = "pollSubmitCallback";
  if (
    formContext.postSubmitCallback &&
    !formContext.postSubmitCallback.map((cb) => cb.idSchemaId).includes(idSchemaId) &&
    !formContext.postSubmitCallback.map((cb) => cb.type).includes(callbackType)
  ) {
    formContext.postSubmitCallback.push({
      idSchemaId: props.idSchema.$id,
      type: callbackType,
      callback: async (newDocumentId?: number) => {
        await submit(newDocumentId);
      },
    });
  }

  return (
    <>
      {validationMessage.length > 0 &&
        validationMessage.map((errMsg) => (
          <Alert key={errMsg} severity="error">
            {errMsg}
          </Alert>
        ))}
      <CategoryObjectTemplate {...props} />
    </>
  );
};

export default PollSubmitCategoryTemplate;
